import React, { useRef, useEffect, useState, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBBtn } from 'mdb-react-ui-kit'
import ContestStore, { WINNER_AMOUNT } from '../Contest.store'
import wheelSpinSrc from '../assets/wheel-spin.mp3'
import winnerSrc from '../assets/winner.mp3'

// Play one tick per slice — throttled so ticks don't overlap at high speed
let lastTickTime = 0
const MIN_TICK_INTERVAL = 150 // ms between ticks
const playTick = () => {
  const now = performance.now()
  if (now - lastTickTime < MIN_TICK_INTERVAL) return
  lastTickTime = now
  try {
    const tick = new Audio(wheelSpinSrc)
    tick.play()
  } catch (e) {}
}

const playWinFanfare = () => {
  try {
    const winAudio = new Audio(winnerSrc)
    winAudio.play()
  } catch (e) {}
}

const WHEEL_COLORS = [
  '#e74c3c',
  '#3498db',
  '#f1c40f',
  '#1abc9c',
  '#9b59b6',
  '#e67e22',
  '#e91e63',
  '#00bcd4',
  '#2ecc71',
  '#ff9800',
  '#673ab7',
  '#009688',
  '#f44336',
  '#2196f3',
  '#ffeb3b',
  '#4caf50',
]

const WheelSpinner = observer(({ onSpinComplete }) => {
  const canvasRef = useRef(null)
  const rotationRef = useRef(0)
  const animFrameRef = useRef(null)
  const lastTickSlice = useRef(-1)
  const [spinning, setSpinning] = useState(false)

  const candidates = ContestStore.wheelCandidates
  const isPlaying = ContestStore.isPlaying
  const isFullscreen = ContestStore.fullscreen
  const hideNamesList = ContestStore.hideNamesList

  const CANVAS_SIZE = isFullscreen ? 900 : isPlaying ? (hideNamesList ? 720 : 620) : 500

  const getLabel = contestant => {
    const name = contestant.u_name || `${contestant.u_fname || ''} ${contestant.u_lname || ''}`.trim()
    return name.length > 16 ? name.substring(0, 14) + '..' : name
  }

  const drawWheel = useCallback(
    rotation => {
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.width = CANVAS_SIZE
      canvas.height = CANVAS_SIZE
      const ctx = canvas.getContext('2d')
      const size = canvas.width
      const cx = size / 2
      const cy = size / 2
      const radius = cx - 15
      const items = candidates.length > 0 ? candidates : [{ u_name: 'No Candidates' }]

      ctx.clearRect(0, 0, size, size)

      const sliceAngle = (2 * Math.PI) / items.length

      items.forEach((item, i) => {
        const start = rotation + i * sliceAngle
        const end = start + sliceAngle

        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.arc(cx, cy, radius, start, end)
        ctx.closePath()
        ctx.fillStyle = WHEEL_COLORS[i % WHEEL_COLORS.length]
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()

        // Label
        ctx.save()
        ctx.translate(cx, cy)
        ctx.rotate(start + sliceAngle / 2)
        const fontSize = items.length > 25 ? 14 : items.length > 15 ? 16 : items.length > 8 ? 18 : 22
        ctx.fillStyle = '#fff'
        ctx.font = `bold ${fontSize}px Arial`
        ctx.textAlign = 'right'
        ctx.textBaseline = 'middle'
        ctx.shadowColor = 'rgba(0,0,0,0.7)'
        ctx.shadowBlur = 3
        ctx.shadowOffsetX = 1
        ctx.shadowOffsetY = 1

        // Show trophy for winners still on wheel
        const isWinner = item.winnings === WINNER_AMOUNT
        ctx.fillText((isWinner ? '\u{1F3C6} ' : '') + getLabel(item), radius - 10, 0)
        ctx.restore()
      })

      // Center hub
      ctx.shadowColor = 'transparent'
      ctx.shadowBlur = 0
      ctx.beginPath()
      ctx.arc(cx, cy, 24, 0, 2 * Math.PI)
      ctx.fillStyle = '#2c3e50'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 3
      ctx.stroke()

      ctx.fillStyle = '#f1c40f'
      ctx.font = 'bold 13px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('$100', cx, cy)

      // Pointer
      ctx.beginPath()
      ctx.moveTo(size - 5, cy - 16)
      ctx.lineTo(size - 5, cy + 16)
      ctx.lineTo(size - 40, cy)
      ctx.closePath()
      ctx.fillStyle = '#2c3e50'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.stroke()
    },
    [candidates, CANVAS_SIZE]
  )

  useEffect(() => {
    drawWheel(rotationRef.current)
  }, [drawWheel, candidates.length])

  const spinWheel = () => {
    if (spinning || ContestStore.spinsRemaining <= 0 || candidates.length === 0 || ContestStore.pendingWinner) return
    setSpinning(true)
    lastTickSlice.current = -1

    const duration = 5000 + Math.random() * 2000
    const totalSpin = Math.PI * 2 * (8 + Math.random() * 5)
    const startRotation = rotationRef.current
    const startTime = performance.now()
    const sliceAngle = (2 * Math.PI) / candidates.length
    const easeOut = t => 1 - Math.pow(1 - t, 4)

    const animate = now => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOut(progress)

      rotationRef.current = startRotation + totalSpin * eased
      drawWheel(rotationRef.current)

      const normalized = (2 * Math.PI - (rotationRef.current % (2 * Math.PI))) % (2 * Math.PI)
      const currentSlice = Math.floor(normalized / sliceAngle)
      if (currentSlice !== lastTickSlice.current) {
        lastTickSlice.current = currentSlice
        playTick()
      }

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(animate)
      } else {
        setSpinning(false)
        const winIdx = Math.floor(normalized / sliceAngle) % candidates.length
        const winner = candidates[winIdx]

        if (winner) {
          playWinFanfare()
          ContestStore.recordWin(winner.user_id)
          if (onSpinComplete) {
            onSpinComplete(winner.user_id)
          }
        }
      }
    }

    animFrameRef.current = requestAnimationFrame(animate)
  }

  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    }
  }, [])

  return (
    <div className='wheel-spinner-component'>
      <h5 className='text-center mb-3'>Wheel of Love</h5>

      <div className='text-center'>
        <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} style={{ maxWidth: '100%' }} />
      </div>

      {isPlaying && (
        <div className='text-center mt-3'>
          <p>
            Spins Remaining: <strong>{ContestStore.spinsRemaining}</strong> / {ContestStore.totalSpins}
            <br />
            <small className='text-muted'>
              ({ContestStore.visibleContestants.length} agents = {ContestStore.totalSpins} spin
              {ContestStore.totalSpins !== 1 ? 's' : ''}, 1 per 3 agents)
            </small>
          </p>
          <MDBBtn
            type='button'
            color='success'
            size='lg'
            onClick={spinWheel}
            disabled={spinning || ContestStore.spinsRemaining <= 0 || !!ContestStore.pendingWinner}
          >
            {spinning
              ? 'Spinning...'
              : ContestStore.pendingWinner
              ? 'Dismiss winner first'
              : ContestStore.spinsRemaining <= 0
              ? 'All Spins Used'
              : 'Spin the Wheel!'}
          </MDBBtn>
        </div>
      )}
    </div>
  )
})

export default WheelSpinner
